import React from 'react';
import { useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import clsx from 'clsx';

import { logo, logoDisabled } from 'helpers/ticketSalesImages';
import { getTotalSeatResavation } from 'helpers/trips';
import { currencyFormat } from 'helpers/currencyFormat';
import { calcSeatFare, maxSeat } from 'helpers/ticketSales';
import { getHoursFromToIoSoDate, getMinutesFromToIoSoDate } from 'helpers';

import { Car, ConventionalSeat, Pedestrian } from 'icons/Location_Transport';
import { Close } from 'icons/Tools_Interface';
import { ArrowRight } from 'icons/Arrows';

import { TransportStrategy } from '../../../../../../strategies/transport';
import { SEAT_TYPE } from '../../index';
import SeatMap from '../SeatMap';
import { ApplicationState } from 'store';
import {
  ItineraryListInterface,
  TicktDataInterface,
} from 'store/ducks/publicStore/types';

interface SeatEntry {
  seatNumber: number;
  value: number;
  id: string;
  floor: number;
  type: number;
}

interface TripCardProps {
  trip: ItineraryListInterface;
  transport: TransportStrategy;
  selectionKey: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onSelect: (trip: ItineraryListInterface) => void;
  seatField: { [tripScheduleId: string]: SeatEntry[] };
  setSeatField: React.Dispatch<
    React.SetStateAction<{ [tripScheduleId: string]: SeatEntry[] }>
  >;
  ocupedSeat: { [tripScheduleId: string]: string[] };
  floor: number;
  setFloor: (floor: number) => void;
  XL: boolean;
  SM: boolean;
  MD: boolean;
  LG: boolean;
  ticketData: TicktDataInterface;
  classes: Record<string, string>;
}

const TripCard: React.FC<TripCardProps> = ({
  trip,
  transport,
  selectionKey,
  isCollapsed,
  onToggleCollapse,
  onSelect,
  seatField,
  setSeatField,
  ocupedSeat,
  floor,
  setFloor,
  XL,
  SM,
  MD,
  LG,
  ticketData,
  classes,
}) => {
  const TransportIcon = transport.TransportIcon;
  const isRescheduling = useSelector(
    (state: ApplicationState) =>
      state.publicStore.step.confirmRescheduling.toShow,
  );
  const previousAmount = useSelector(
    (state: ApplicationState) => state.publicStore.rescheduleData.amount,
  );
  // Mesmo cálculo da listagem normal
  const currentTripValue = calcSeatFare(trip);
  // Diferença = valor da listagem - valor da passagem antiga
  const priceDifference = Number(
    (currentTripValue - (previousAmount || 0)).toFixed(2),
  );
  const formattedPrice = isRescheduling
    ? `+ ${currencyFormat({ value: Math.max(0, priceDifference) })}`
    : currencyFormat({ value: currentTripValue });

  return (
    <Grid
      xl={12}
      lg={12}
      md={12}
      sm={12}
      xs={12}
      className={
        trip.thereAreVacancies
          ? classes.contentTravel
          : clsx(classes.contentTravel, classes.contentTravelDisabled)
      }>
      <Grid container xl={12} lg={12} md={12} sm={12} xs={12}>
        {/* Coluna esquerda: logo + horário */}
        <Grid
          alignItems='center'
          container
          xl={3}
          lg={3}
          md={3}
          sm={12}
          xs={12}>
          <Grid xl={12} lg={12} md={12} sm={12} xs={12}>
            <img
              src={trip.thereAreVacancies ? logo : logoDisabled}
              alt='company illustration'
            />
          </Grid>
          <Grid xl={12} lg={12} md={12} sm={12} xs={12}>
            <Box
              display='flex'
              alignItems='center'
              pt={transport.isSea ? '0.5rem' : '0'}>
              {transport.showBoardingTime(trip) && (
                <Typography>
                  {getHoursFromToIoSoDate(trip.open)}:
                  {getMinutesFromToIoSoDate(trip.open)}
                </Typography>
              )}
              <Box p='0.5rem'>
                <TransportIcon
                  fill='#0094FF'
                  width='1.25rem'
                  height='1.25rem'
                />
              </Box>
              <Box
                display='flex'
                alignItems='center'
                pt={transport.isSea ? '0.5rem' : '0'}>
                <Typography>
                  {getHoursFromToIoSoDate(trip.start)}:
                  {getMinutesFromToIoSoDate(trip.start)}
                </Typography>
                {trip?.seats?.length > 0 && (
                  <Box ml='0.25rem' display='flex' alignItems='center'>
                    <ArrowRight fill='#0094ff' width='1rem' height='1rem' />
                    <Typography style={{ paddingLeft: '0.25rem' }}>
                      {getHoursFromToIoSoDate(trip.end)}:
                      {getMinutesFromToIoSoDate(trip.end)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Coluna central: nome e vagas */}
        <Grid
          alignItems='center'
          container
          xl={7}
          lg={7}
          md={7}
          sm={12}
          xs={12}>
          <Grid xl={12} lg={12} md={12} sm={12} xs={12}>
            <Box pl='0.625rem'>
              <Typography>{trip.sectionName}</Typography>
            </Box>
          </Grid>
          <Grid xl={12} lg={12} md={12} sm={12} xs={12}>
            {trip.seats?.length === 0 && (
              <Box pl='0.5rem' pt='0.5rem' display='flex' alignItems='center'>
                {transport.showVehicleCapacity && (
                  <>
                    <Car fill='#010303' width='1.25rem' height='1.25rem' />
                    <Box p='0.25rem' pr='0.25'>
                      <Typography className={classes.subTitle}>
                        {Number(trip.vehicleCapacity) > 0
                          ? `${trip?.vehicleCapacity} vagas`
                          : 'Esgotado'}
                      </Typography>
                    </Box>
                  </>
                )}
                <Pedestrian fill='#010303' width='1.5rem' height='1.5rem' />
                <Box p='0.25rem'>
                  <Typography className={classes.subTitle}>
                    {Number(trip.footCapacity) > 0
                      ? `${trip?.footCapacity} vagas${getTotalSeatResavation(
                          trip,
                        )}`
                      : 'Esgotado'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>

          {trip.seats?.length > 0 && (
            <>
              <Grid xl={12} lg={12} md={12} sm={12} xs={12}>
                <Box display='flex' alignItems='center'>
                  <Box p='0.5rem'>
                    <ConventionalSeat width='1.5rem' height='1.5rem' />
                  </Box>
                  <Typography>
                    {trip?.seats?.filter((e: any) => e?.enabled)?.length || 0}{' '}
                    Livres
                  </Typography>
                </Box>
              </Grid>
              <Grid xl={12} lg={12} md={12} sm={12} xs={12}>
                <Box className={classes.seatType}>
                  <Box p='0.5rem'>
                    {
                      SEAT_TYPE?.[
                        maxSeat(
                          trip.seats.filter((e) => e.enabled),
                        ) as keyof typeof SEAT_TYPE
                      ]?.ICON
                    }
                  </Box>
                  <Typography>
                    {
                      SEAT_TYPE?.[
                        maxSeat(
                          trip.seats.filter((e) => e.enabled),
                        ) as keyof typeof SEAT_TYPE
                      ]?.LABEL
                    }
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
        </Grid>

        {/* Coluna direita: preço + fechar */}
        <Grid
          alignItems='baseline'
          justifyContent='flex-end'
          container
          xl={2}
          lg={2}
          md={2}
          sm={2}
          xs={12}>
          <Grid xl={12} lg={12} md={12} sm={12} xs={12}>
            <Box
              mt='0.75rem'
              display='flex'
              width='100%'
              justifyContent='space-between'>
              <Typography align='right' style={{ fontWeight: 600 }}>
                {formattedPrice}
              </Typography>
              {isCollapsed && (
                <IconButton
                  onClick={() => {
                    setSeatField((s) => ({ ...s, [selectionKey]: [] }));
                    onToggleCollapse();
                  }}
                  style={{
                    background: '#fff',
                    width: '1.25rem',
                    height: '1.25rem',
                    padding: '4px',
                  }}>
                  <Close width='0.75rem' height='0.75rem' fill='#000' />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* Mapa de poltronas (apenas ônibus) */}
      {trip?.seats?.length > 0 && (
        <Collapse in={isCollapsed} timeout='auto' unmountOnExit>
          <SeatMap
            trip={trip}
            selectionKey={selectionKey}
            seatField={seatField}
            setSeatField={setSeatField}
            ocupedSeat={ocupedSeat}
            floor={floor}
            setFloor={setFloor}
            XL={XL}
            SM={SM}
            MD={MD}
            LG={LG}
            ticketData={ticketData}
            classes={classes}
          />
        </Collapse>
      )}

      {/* Botão de seleção */}
      <Grid
        alignItems='center'
        justifyContent='flex-end'
        container
        xl={12}
        lg={12}
        md={12}
        sm={12}
        xs={12}>
        <Box
          mt='0.75rem'
          display='flex'
          width='100%'
          alignItems='end'
          justifyContent='flex-end'>
          <Button
            disabled={
              !trip.thereAreVacancies ||
              (isCollapsed && seatField?.[selectionKey]?.length === 0)
            }
            className={classes.selecetTravel}
            onClick={() => onSelect(trip)}>
            <ArrowRight width='0.75rem' height='0.75rem' fill='#fff' />
            <Typography style={{ marginLeft: '0.25rem' }}>
              {isCollapsed ? 'Continuar' : 'Selecionar'}
            </Typography>
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default TripCard;
